/**
 * Contact page and authenticated issue report form.
 */
import { useState } from "react";
import { submitIssueReport } from "../scripts/api";
import PageShell from "../components/ui/PageShell";
import GlassPanel from "../components/ui/GlassPanel";
import TextField from "../components/ui/TextField";
import SelectField from "../components/ui/SelectField";
import Button from "../components/ui/Button";
import Alert from "../components/ui/Alert";
import { useLoadingOverlay } from "../context/LoadingOverlayContext";

const ISSUE_CATEGORIES = [
  "website",
  "game",
  "authentication",
  "subscriptions",
  "payment",
  "other",
];

function isAllowedImageUrl(value) {
  if (!value) return true;

  try {
    const url = new URL(value);

    if (url.protocol !== "https:") {
      return false;
    }

    const hostname = url.hostname.toLowerCase();
    if (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "::1" ||
      hostname.endsWith(".local")
    ) {
      return false;
    }

    const pathname = url.pathname.toLowerCase();
    return [".png", ".jpg", ".jpeg", ".webp"].some((ext) => pathname.endsWith(ext));
  } catch {
    return false;
  }
}

export default function Contact({ session, loading }) {
  const { showLoading, hideLoading } = useLoadingOverlay();
  const [category, setCategory] = useState("");
  const [occurredAt, setOccurredAt] = useState("");
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isAuthenticated = !!session?.authenticated;
  const email = session?.user?.email || "";

  const categoryOptions = ISSUE_CATEGORIES.map((item) => ({
    value: item,
    label: item,
  }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!isAuthenticated || !email) {
      setError("You must be signed in with a valid email.");
      return;
    }

    if (!ISSUE_CATEGORIES.includes(category)) {
      setError("Please choose a valid category.");
      return;
    }

    if (!occurredAt) {
      setError("Please choose when the issue happened.");
      return;
    }

    const occurredDate = new Date(occurredAt);
    if (Number.isNaN(occurredDate.getTime())) {
      setError("Issue time is invalid.");
      return;
    }

    if (occurredDate > new Date()) {
      setError("Issue time cannot be in the future.");
      return;
    }

    const trimmedDescription = description.trim();
    if (trimmedDescription.length < 20 || trimmedDescription.length > 5000) {
      setError("Description must be between 20 and 5000 characters.");
      return;
    }

    const trimmedImageUrl = imageUrl.trim();
    if (trimmedImageUrl && !isAllowedImageUrl(trimmedImageUrl)) {
      setError("Image URL must be a valid HTTPS PNG, JPG, JPEG, or WebP link.");
      return;
    }

    setSubmitting(true);
    showLoading("Submitting report…");
    try {
      await submitIssueReport({
        category,
        occurredAt: occurredDate.toISOString(),
        description: trimmedDescription,
        imageUrl: trimmedImageUrl,
      });

      setCategory("");
      setOccurredAt("");
      setDescription("");
      setImageUrl("");
      setSuccess("Issue report submitted successfully.");
    } catch (err) {
      setError(err.message || "Failed to submit issue report.");
    } finally {
      hideLoading();
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      <div className="space-y-10">
        <GlassPanel>
          <div className="space-y-4">
            <h1 className="text-2xl font-bold text-white">Contact</h1>
            <hr className="border-white/10" />
            <p className="leading-relaxed text-white/90">
              If you have any questions you can email our team at admin@pax-studios.org.
            </p>
          </div>
        </GlassPanel>

        {!loading && isAuthenticated ? (
          <GlassPanel>
            <div className="space-y-5">
              <h2 className="text-2xl font-bold text-white">Report issue</h2>
              <p className="text-sm text-white/55">
                Describe what happened. Fields marked with * are required.
              </p>
              <hr className="border-white/10" />

              {!email ? (
                <Alert severity="info">
                  You must be signed in with a valid email to report an issue.
                </Alert>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div
                    className={
                      "space-y-5 rounded-xl border border-white/10 bg-white/[0.04] p-5 shadow-sm backdrop-blur-sm " +
                      "transition-colors duration-200 hover:border-white/15"
                    }
                  >
                    {error ? <Alert severity="error">{error}</Alert> : null}
                    {success ? <Alert severity="success">{success}</Alert> : null}

                    <TextField
                      id="signed-in-email"
                      label="Signed in as"
                      value={email}
                      onChange={() => {}}
                      readOnly
                    />

                    <SelectField
                      id="issue-category"
                      label="Category"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      options={categoryOptions}
                      required
                      disabled={submitting}
                    />

                    <TextField
                      id="occurred-at"
                      label="When did it happen?"
                      type="datetime-local"
                      value={occurredAt}
                      onChange={(e) => setOccurredAt(e.target.value)}
                      required
                      disabled={submitting}
                    />

                    <TextField
                      id="issue-description"
                      label="Description"
                      multiline
                      rows={6}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      maxLength={5000}
                      helperText="20 to 5000 characters"
                      required
                      disabled={submitting}
                    />

                    <TextField
                      id="image-url"
                      label="Image URL (optional)"
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      helperText="HTTPS link to a PNG, JPG, JPEG, or WebP image"
                      disabled={submitting}
                    />

                    <div className="flex flex-col gap-3 border-t border-white/10 pt-5 sm:flex-row sm:items-center">
                      <Button
                        type="submit"
                        variant="solid"
                        disabled={submitting}
                        className="min-h-[2.75rem] w-full min-w-[12rem] sm:w-auto"
                      >
                        {submitting ? "Submitting…" : "Submit report"}
                      </Button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </GlassPanel>
        ) : null}
      </div>
    </PageShell>
  );
}
